import { ReactNode, useRef } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { IconCaret } from "src/components/icons-mui/caret";
import { Button, IButtonProps } from "src/components-mui/base/button/button";
import { useContextMenu } from "src/components-mui/base/context-menu/use-context-menu";
import { useFlipPlacement } from "src/components-mui/base/context-menu/use-flip-placement";
import { ContextMenu } from "src/components-mui/common/context-menu/context-menu";
import { toSxArray } from "src/utils/mui/sx/sx";

const DEFAULT_MENU_MAX_HEIGHT = 256;
const DEFAULT_MENU_WIDTH = 250;

type TSelectMenuVariant = "menu" | "selectedMenu";
type TSelectMenuButtonVariant = NonNullable<IButtonProps["variant"]>;
type TSelectMenuButtonColor = NonNullable<IButtonProps["color"]>;
type TSelectMenuButtonSize = NonNullable<IButtonProps["size"]>;
type TSelectMenuIcon = Exclude<ReactNode, undefined>;

const baseSx = {
    width: "100%",
    justifyContent: "space-between",
    overflow: "hidden",
    fontWeight: 400,
    borderColor: "gray.400",
    paddingLeft: 2,
    paddingRight: 2,

    "& .MuiButton-endIcon": {
        marginLeft: 1,
        flexShrink: 0,
    },
    "&:hover": {
        borderColor: "gray.800",
        backgroundColor: "transparent",
    },
    "&:active": {
        borderColor: "gray.800",
        backgroundColor: "transparent",
        boxShadow: (theme: Theme) => `0 0 0 1px ${theme.palette.gray[800]}`,
    },
    '&[aria-expanded="true"]': {
        backgroundColor: "transparent",
        borderColor: "gray.800",
        boxShadow: (theme: Theme) => `0 0 0 1px ${theme.palette.gray[800]}`,
    },
} as const;

const labelSx = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
    display: "block",
    lineHeight: 1.5,
    paddingBlock: "1px",
} as const;

interface ISelectMenuRenderProps {
    /** Closes the menu. Call it from a menu item's `onClick` after acting on the selection. */
    closeMenu: () => void;
}

interface ISelectMenuSlotProps {
    list?: Record<string, unknown>;
    paper?: { sx?: SxProps<Theme> };
}

export interface ISelectMenuProps {
    /** DOM id for the menu. */
    id: string;
    /** Content rendered inside the trigger button (e.g. the current selection label). */
    label: ReactNode;
    /** Render prop for the menu items. Receives `closeMenu` so items can close on selection. */
    children: (renderProps: ISelectMenuRenderProps) => ReactNode;
    /** DOM id for the trigger button. Also wires `aria-labelledby` on the menu list. */
    buttonId?: string;
    disabled?: boolean;
    /** `selectedMenu` focuses (and scrolls to) the selected item when the menu opens. Defaults to `menu`. */
    menuVariant?: TSelectMenuVariant;
    buttonVariant?: TSelectMenuButtonVariant;
    color?: TSelectMenuButtonColor;
    size?: TSelectMenuButtonSize;
    startIcon?: TSelectMenuIcon;
    /** Overrides the default rotating caret. */
    endIcon?: TSelectMenuIcon;
    buttonSx?: SxProps<Theme>;
    anchorGap?: number;
    anchorOrigin?: { vertical: "top" | "center" | "bottom"; horizontal: "left" | "center" | "right" };
    transformOrigin?: { vertical: "top" | "center" | "bottom"; horizontal: "left" | "center" | "right" };
    /** Distance (px) the menu is kept from the viewport edge. Defaults to `null` (no clamping) so the menu follows the trigger on scroll. */
    marginThreshold?: number | null;
    /** Width (px) of the dropdown menu. Defaults to 250. */
    menuWidth?: number;
    /** Max height (px) of the dropdown menu. Defaults to 256. */
    menuMaxHeight?: number;
    slotProps?: ISelectMenuSlotProps;
}

/**
 * A select-like dropdown menu: a full-width trigger `Button` (with a rotating caret)
 * that opens a `ContextMenu` styled for select-like scenarios.
 */
export const SelectMenu = ({
    label,
    children,
    id,
    buttonId,
    disabled = false,
    menuVariant = "menu",
    buttonVariant = "outlined",
    color = "gray",
    size = "medium",
    startIcon,
    endIcon,
    buttonSx,
    anchorGap = 1,
    anchorOrigin = { vertical: "bottom", horizontal: "left" },
    transformOrigin = { vertical: "top", horizontal: "left" },
    marginThreshold = null,
    menuWidth = DEFAULT_MENU_WIDTH,
    menuMaxHeight = DEFAULT_MENU_MAX_HEIGHT,
    slotProps,
}: ISelectMenuProps) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const { isOpen, onOpen, onClose } = useContextMenu();
    const {
        updateFlipped,
        anchorOrigin: resolvedAnchorOrigin,
        transformOrigin: resolvedTransformOrigin,
        anchorGap: resolvedAnchorGap,
    } = useFlipPlacement({ anchorRef, isOpen, menuMaxHeight, anchorGap, anchorOrigin, transformOrigin });

    const handleTriggerClick = () => {
        if (isOpen) {
            onClose();
            return;
        }
        updateFlipped();
        onOpen();
    };

    const resolvedEndIcon = endIcon ?? (
        <IconCaret
            sx={{
                width: 24,
                height: 24,
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms ease",
            }}
        />
    );

    const listSlotProps = {
        ...(buttonId ? { "aria-labelledby": buttonId } : {}),
        ...slotProps?.list,
    };

    const mergedSx: SxProps<Theme> = buttonSx ? [baseSx, ...toSxArray(buttonSx)] : baseSx;

    return (
        <>
            <Box ref={anchorRef} sx={{ width: "100%" }}>
                <Button
                    id={buttonId}
                    aria-controls={isOpen ? id : undefined}
                    aria-haspopup="menu"
                    aria-expanded={isOpen ? "true" : undefined}
                    variant={buttonVariant}
                    color={color}
                    size={size}
                    disableElevation={true}
                    disabled={disabled}
                    startIcon={startIcon}
                    onClick={handleTriggerClick}
                    endIcon={resolvedEndIcon}
                    sx={mergedSx}
                >
                    <Box component="span" sx={labelSx}>
                        {label}
                    </Box>
                </Button>
            </Box>

            <ContextMenu
                id={id}
                open={isOpen}
                anchorRef={anchorRef}
                onClose={onClose}
                anchorGap={resolvedAnchorGap}
                anchorOrigin={resolvedAnchorOrigin}
                transformOrigin={resolvedTransformOrigin}
                marginThreshold={marginThreshold}
                variant={menuVariant}
                disableAutoFocusItem={menuVariant !== "selectedMenu"}
                slotProps={{
                    ...slotProps,
                    list: listSlotProps,
                    paper: {
                        sx: [
                            {
                                width: `${menuWidth}px`,
                                maxHeight: `${menuMaxHeight}px`,
                                overflowX: "hidden",
                                overflowY: "auto",
                                scrollbarWidth: "thin",
                                scrollbarColor: "transparent transparent",
                                "&:hover": {
                                    scrollbarColor: (theme: Theme) => `${theme.palette.gray[400]} transparent`,
                                },
                                "&::-webkit-scrollbar": {
                                    width: 8,
                                    height: 8,
                                },
                                "&::-webkit-scrollbar-track": {
                                    backgroundColor: "transparent",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    borderRadius: 4,
                                    backgroundColor: "transparent",
                                },
                                "&:hover::-webkit-scrollbar-thumb": {
                                    backgroundColor: "gray.400",
                                },
                                "& .MuiMenu-list": {
                                    paddingTop: 1,
                                    paddingBottom: 1,
                                },
                            },
                            ...toSxArray(slotProps?.paper?.sx),
                        ],
                    },
                }}
            >
                {children({ closeMenu: onClose })}
            </ContextMenu>
        </>
    );
};
